import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ToastContext } from './toast/toastContext';
import { ToastCard } from './toast/ToastCard';
import { positionStyles } from './toast/toastStyles';
import type { ToastItem, ToastProviderProps } from './toast/toastTypes';

// Oeffentlicher Einstiegspunkt: Hook und Typen werden hier weiterexportiert,
// damit der Importpfad `@/components/ui` unveraendert bleibt.
export { useToast } from './toast/toastContext';
export type { ToastItem, ToastProviderProps, ToastProps } from './toast/toastTypes';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Generate unique ID
 */
let toastIdCounter = 0;
const generateId = () => `toast-${++toastIdCounter}`;

/**
 * ToastProvider component
 *
 * @example
 * ```tsx
 * <ToastProvider position="top-right">
 *   <App />
 * </ToastProvider>
 *
 * // In a component:
 * const { addToast } = useToast();
 *
 * addToast({
 *   title: 'Success!',
 *   description: 'Your changes have been saved.',
 *   variant: 'success',
 *   duration: 5000,
 * });
 * ```
 */
export const ToastProvider = ({
  children,
  position = 'top-right',
  maxToasts = 5,
}: ToastProviderProps) => {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback(
    (toast: Omit<ToastItem, 'id'>) => {
      const id = generateId();
      const newToast: ToastItem = {
        ...toast,
        id,
        duration: toast.duration ?? 5000,
      };

      setToasts((prev) => {
        const updated = [newToast, ...prev];
        return updated.slice(0, maxToasts);
      });

      return id;
    },
    [maxToasts]
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const toastContainer = (
    <div
      className={cn('fixed z-[100] flex flex-col gap-2', positionStyles[position])}
      aria-label={t('a11y.notifications')}
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <ToastCard
            key={toast.id}
            toast={toast}
            position={position}
            onDismiss={() => removeToast(toast.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearToasts }}>
      {children}
      {typeof document !== 'undefined' && createPortal(toastContainer, document.body)}
    </ToastContext.Provider>
  );
};

/**
 * Convenience functions for creating toasts
 */
export const toast = {
  success: (title: string, options?: Partial<Omit<ToastItem, 'id' | 'variant' | 'title'>>) => ({
    title,
    variant: 'success' as const,
    ...options,
  }),
  error: (title: string, options?: Partial<Omit<ToastItem, 'id' | 'variant' | 'title'>>) => ({
    title,
    variant: 'error' as const,
    ...options,
  }),
  warning: (title: string, options?: Partial<Omit<ToastItem, 'id' | 'variant' | 'title'>>) => ({
    title,
    variant: 'warning' as const,
    ...options,
  }),
  info: (title: string, options?: Partial<Omit<ToastItem, 'id' | 'variant' | 'title'>>) => ({
    title,
    variant: 'info' as const,
    ...options,
  }),
};

export default ToastProvider;
